// WikiReadTracker.jsx — read-receipt right panel (who has / hasn't) + read-confirm bar.
import React, { useEffect, useState, useCallback } from 'react';
import { UserCheck, X, Check, CheckCircle2, Clock, Bell, Send, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useWikiApi } from '../../hooks/useWiki';
import { relativeTime } from '../../lib/wikiConstants';

const PALETTE = ['bg-stone-900', 'bg-rose-500', 'bg-emerald-600', 'bg-stone-900', 'bg-amber-500', 'bg-cyan-600', 'bg-blue-500', 'bg-fuchsia-600'];
function Avatar({ name, size = 28 }) {
    const initials = (name || '?').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
    const color = PALETTE[(name || '').length % PALETTE.length];
    return <div className={`${color} rounded-full text-white font-semibold flex items-center justify-center shrink-0`} style={{ width: size, height: size, fontSize: size * 0.38 }}>{initials}</div>;
}

function useRoster(pageId, bump) {
    const api = useWikiApi();
    const [roster, setRoster] = useState(null);
    useEffect(() => { let m = true; api.readers(pageId).then(d => m && setRoster(d)).catch(() => m && setRoster(null)); return () => { m = false; }; }, [pageId, bump]); // eslint-disable-line
    return roster;
}

export default function WikiReadTracker({ pageId, isAdmin, onClose }) {
    const api = useWikiApi();
    const [bump, setBump] = useState(0);
    const roster = useRoster(pageId, bump);
    const remind = useCallback(async (ids) => {
        try { const r = await api.remind(pageId, { user_ids: ids }); toast.success(`Reminded ${r.reminded} ${r.reminded === 1 ? 'person' : 'people'}`); }
        catch { toast.error('Could not send reminders'); }
    }, [api, pageId]);

    const done = roster?.done || [];
    const pending = roster?.pending || [];
    const pct = roster?.pct ?? 0;

    return (
        <div className="w-[320px] border-l border-stone-200 bg-white flex flex-col h-full">
            <div className="h-12 px-4 flex items-center gap-2 border-b border-stone-200">
                <UserCheck className="w-4 h-4 text-emerald-600" />
                <span className="text-[13px] wiki-display font-bold text-stone-900 flex-1">Read tracker</span>
                <button onClick={onClose} className="w-7 h-7 rounded hover:bg-stone-100 text-stone-500 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-4 py-3.5 border-b border-stone-200 flex items-center gap-3">
                <div className="relative w-12 h-12 shrink-0">
                    <svg viewBox="0 0 40 40" className="w-full h-full -rotate-90">
                        <circle cx="20" cy="20" r="16" stroke="#E2E8F0" strokeWidth="5" fill="none" />
                        <circle cx="20" cy="20" r="16" stroke="#10B981" strokeWidth="5" fill="none" strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 16 * pct / 100} ${2 * Math.PI * 16}`} />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-stone-900">{pct}%</div>
                </div>
                <div>
                    <div className="text-[13px] wiki-display font-bold text-stone-900">{done.length} of {roster?.total ?? 0} read</div>
                    <div className="text-[11px] text-stone-500">{pending.length} still to acknowledge</div>
                </div>
            </div>
            <div className="flex-1 overflow-auto p-3 space-y-4">
                <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-600 mb-1.5 px-1 flex items-center gap-1"><Check className="w-3 h-3" /> Acknowledged ({done.length})</div>
                    <div className="space-y-0.5">
                        {done.map((p) => (
                            <div key={p.user_id} className="flex items-center gap-2.5 px-1.5 py-1.5 rounded-lg hover:bg-stone-50">
                                <Avatar name={p.name} />
                                <div className="min-w-0 flex-1"><div className="text-[12.5px] font-medium text-stone-900 truncate">{p.name}</div>{p.title && <div className="text-[10.5px] text-stone-500 truncate">{p.title}</div>}</div>
                                <div className="text-[10.5px] text-stone-500 inline-flex items-center gap-1 shrink-0"><CheckCircle2 className="w-3 h-3 text-emerald-500" />{relativeTime(p.confirmed_at)}</div>
                            </div>
                        ))}
                        {done.length === 0 && <div className="text-[11px] text-stone-400 px-1.5 py-1">No one yet.</div>}
                    </div>
                </div>
                {pending.length > 0 && (
                    <div>
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-600 mb-1.5 px-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Not yet read ({pending.length})</div>
                        <div className="space-y-0.5">
                            {pending.map((p) => (
                                <div key={p.user_id} className="flex items-center gap-2.5 px-1.5 py-1.5 rounded-lg hover:bg-stone-50">
                                    <Avatar name={p.name} />
                                    <div className="min-w-0 flex-1"><div className="text-[12.5px] font-medium text-stone-900 truncate">{p.name}</div>{p.title && <div className="text-[10.5px] text-stone-500 truncate">{p.title}</div>}</div>
                                    {isAdmin && <button onClick={() => remind([p.user_id])} className="text-[11px] font-semibold text-stone-900 hover:text-stone-900 inline-flex items-center gap-1 shrink-0"><Bell className="w-3 h-3" /> Remind</button>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {isAdmin && pending.length > 0 && (
                <div className="p-3 border-t border-stone-200">
                    <button onClick={() => remind(null)} className="w-full h-9 rounded-lg bg-stone-900 text-white text-[12px] font-semibold inline-flex items-center justify-center gap-1.5"><Send className="w-3.5 h-3.5" /> Remind all {pending.length} pending</button>
                </div>
            )}
        </div>
    );
}

export function WikiReadConfirmBar({ pageId, confirmed, onConfirmed, onShowReaders }) {
    const api = useWikiApi();
    const roster = useRoster(pageId, confirmed ? 1 : 0);
    const [busy, setBusy] = useState(false);
    const done = roster?.done || [];
    const total = roster?.total ?? 0;
    const pct = roster?.pct ?? 0;
    const youDone = confirmed;

    const confirm = async () => {
        setBusy(true);
        try { await api.confirmRead(pageId); onConfirmed && onConfirmed(); toast.success('Marked as read'); }
        catch { toast.error('Could not record acknowledgement'); }
        finally { setBusy(false); }
    };

    return (
        <div className={`border-t px-6 py-3 flex items-center gap-4 ${youDone ? 'border-emerald-200 bg-emerald-50/60' : 'border-stone-300 bg-stone-100/50'}`}>
            <button onClick={onShowReaders} className="flex -space-x-1.5 hover:opacity-80" title="See who has read this">
                {done.slice(0, 4).map((p) => <div key={p.user_id} className="ring-2 ring-white rounded-full"><Avatar name={p.name} size={24} /></div>)}
            </button>
            <div className="min-w-0 flex-1">
                <button onClick={onShowReaders} className="text-[12px] font-semibold text-stone-900 hover:text-stone-800 inline-flex items-center gap-1">{done.length} of {total} people have read this page <ChevronRight className="w-3.5 h-3.5 text-stone-400" /></button>
                <div className="h-1.5 rounded-full bg-white/70 mt-1 overflow-hidden max-w-[220px]"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>
            </div>
            {youDone ? (
                <div className="flex items-center gap-2 text-[12px] font-semibold text-emerald-700"><CheckCircle2 className="w-4 h-4" /> You confirmed — just now</div>
            ) : (
                <button onClick={confirm} disabled={busy} className="h-9 px-4 rounded-lg bg-stone-900 hover:bg-stone-800 disabled:opacity-60 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 shrink-0">
                    <Check className="w-4 h-4" /> I have read &amp; understood this
                </button>
            )}
        </div>
    );
}
